import type { ChannelFormState } from '../types';

type ChannelsBucket = {
  direct_channels: readonly { slug: string }[];
  vonq_channels: readonly { product_id: string }[];
};

/**
 * Selected channels that the Top Picks section offered. A direct channel's
 * form key is its slug and a Vonq product's is its product id, so both are
 * matched on the form key.
 */
export function countTopPicksSelected(
  channels: Record<string, ChannelFormState>,
  topPicks: readonly ChannelsBucket[],
): number {
  const topPickKeys = new Set(
    topPicks.flatMap(bucket => [
      ...bucket.direct_channels.map(channel => channel.slug),
      ...bucket.vonq_channels.map(channel => channel.product_id),
    ]),
  );
  return Object.keys(channels).filter(key => topPickKeys.has(key)).length;
}

export function isMarketplaceBoardSelected(
  channels: Record<string, ChannelFormState>,
): boolean {
  return Object.values(channels).some(channel => channel.vonqDetails != null);
}

/** The opening's saved details as the server held them before this flow saved it. */
export type DetailBaseline = {
  job_description?: string | null;
  state_code?: string | null;
};

/**
 * Job details as the server confirmed saving them. `baseline` is fixed at the
 * opening's first save in the flow, so a later refetch cannot erase a change.
 */
export type ConfirmedDetailSave = {
  description?: string | null;
  stateCode?: string | null;
  baseline: DetailBaseline;
};

// The fields the job-details save persists. The title is never saved to the
// opening, so an edited title is not a persisted detail.
const SAVED_DETAIL_FIELDS = [
  { field: 'description', server: 'job_description' },
  { field: 'stateCode', server: 'state_code' },
] as const;

/**
 * The job-detail fields a server-confirmed save changed from the opening's
 * baseline, across the given openings. An edit the recruiter never saved is
 * not counted.
 */
export function confirmedDetailFields(
  saves: Record<string, ConfirmedDetailSave>,
  openingIds: readonly string[],
): string[] {
  const changed = new Set<string>();
  for (const openingId of openingIds) {
    const saved = saves[openingId];
    if (!saved) continue;

    for (const { field, server } of SAVED_DETAIL_FIELDS) {
      if ((saved[field] ?? '') !== (saved.baseline[server] ?? '')) {
        changed.add(field);
      }
    }
  }
  return [...changed];
}
